import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { ClipLoader } from 'react-spinners';
import { FiX, FiZap } from 'react-icons/fi';
import subscriptionWarrantyService from '../../service/subscriptionWarranty.service';
import AuthService from '../../service/auth.service';
import logo from '../../assets/logo.png';
import { loadRazorpaySdk } from '../../utils/loadRazorpay';

const borderPulse = keyframes`
  0%, 100% {
    opacity: 0.45;
    filter: drop-shadow(0 0 8px rgba(34, 211, 238, 0.35));
  }
  50% {
    opacity: 0.95;
    filter: drop-shadow(0 0 16px rgba(168, 85, 247, 0.55));
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const scanLine = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  15% { opacity: 0.12; }
  85% { opacity: 0.12; }
  100% { transform: translateY(100vh); opacity: 0; }
`;

/** Fixed dock — does not consume layout space at top of page */
const Dock = styled.aside`
  position: fixed;
  z-index: 998;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: min(340px, calc(100vw - 2rem));
  max-height: min(72vh, 560px);
  display: flex;
  flex-direction: column;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }

  @media (max-width: 768px) {
    right: 0.65rem;
    left: 0.65rem;
    top: auto;
    bottom: max(0.75rem, env(safe-area-inset-bottom, 0px));
    transform: none;
    width: auto;
    max-height: min(48vh, 420px);
  }
`;

const Panel = styled(motion.div)`
  position: relative;
  border-radius: 4px 16px 16px 16px;
  padding: 1rem 1rem 1.1rem;
  overflow: hidden;
  background: linear-gradient(
    165deg,
    rgba(12, 10, 28, 0.92) 0%,
    rgba(8, 8, 18, 0.96) 50%,
    rgba(15, 8, 28, 0.94) 100%
  );
  backdrop-filter: blur(16px) saturate(1.2);
  -webkit-backdrop-filter: blur(16px) saturate(1.2);
  border: 1px solid transparent;
  background-clip: padding-box;
  box-shadow:
    0 0 0 1px rgba(34, 211, 238, 0.12),
    0 12px 40px rgba(0, 0, 0, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(
      135deg,
      rgba(34, 211, 238, 0.55) 0%,
      rgba(168, 85, 247, 0.45) 40%,
      rgba(236, 72, 153, 0.35) 100%
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    animation: ${borderPulse} 4s ease-in-out infinite;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(34, 211, 238, 0.8),
      rgba(168, 85, 247, 0.9),
      transparent
    );
    background-size: 200% 100%;
    animation: ${shimmer} 3s linear infinite;
    opacity: 0.7;
    pointer-events: none;
  }
`;

const ScanSweep = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  border-radius: inherit;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 40%;
    background: linear-gradient(
      to bottom,
      transparent,
      rgba(34, 211, 238, 0.04),
      transparent
    );
    animation: ${scanLine} 6s ease-in-out infinite;
  }
`;

const GridBg = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.06;
  background-image:
    linear-gradient(rgba(168, 85, 247, 0.4) 1px, transparent 1px),
    linear-gradient(90deg, rgba(34, 211, 238, 0.35) 1px, transparent 1px);
  background-size: 20px 20px;
  pointer-events: none;
  border-radius: inherit;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.65rem;
  position: relative;
  z-index: 1;
`;

const Kicker = styled.div`
  font-family: 'JetBrains Mono', 'Fira Code', ui-monospace, monospace;
  font-size: 0.62rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(34, 211, 238, 0.85);
  margin-bottom: 0.2rem;
`;

const Title = styled.div`
  font-weight: 800;
  font-size: 0.95rem;
  letter-spacing: 0.04em;
  color: #f5f3ff;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  text-shadow: 0 0 24px rgba(168, 85, 247, 0.4);

  svg {
    color: #22d3ee;
    filter: drop-shadow(0 0 6px rgba(34, 211, 238, 0.6));
  }
`;

const Blurb = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.72rem;
  line-height: 1.45;
  color: rgba(226, 232, 240, 0.72);
  position: relative;
  z-index: 1;
`;

const CourseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  max-height: min(52vh, 360px);
  overflow-y: auto;
  padding-right: 2px;
  position: relative;
  z-index: 1;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(168, 85, 247, 0.35);
    border-radius: 4px;
  }

  @media (max-width: 768px) {
    max-height: min(36vh, 280px);
  }
`;

const CourseCard = styled.div`
  position: relative;
  padding: 0.65rem 0.7rem;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(168, 85, 247, 0.22);
  box-shadow: inset 0 0 20px rgba(34, 211, 238, 0.04);

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    border-radius: 10px 0 0 10px;
    background: linear-gradient(180deg, #22d3ee, #a855f7);
    opacity: 0.85;
  }
`;

const CourseTitle = styled.strong`
  display: block;
  color: #fff;
  font-size: 0.82rem;
  font-weight: 700;
  padding-left: 0.35rem;
`;

const Meta = styled.div`
  font-size: 0.7rem;
  color: rgba(203, 213, 225, 0.85);
  margin-top: 0.3rem;
  padding-left: 0.35rem;
  line-height: 1.4;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
  margin-top: 0.55rem;
  padding-left: 0.35rem;
`;

const PayBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.4rem 0.85rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 800;
  font-size: 0.75rem;
  letter-spacing: 0.03em;
  color: #0c0118;
  background: linear-gradient(135deg, #22d3ee 0%, #a855f7 55%, #ec4899 100%);
  background-size: 140% 100%;
  box-shadow:
    0 0 20px rgba(34, 211, 238, 0.25),
    0 4px 14px rgba(0, 0, 0, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow:
      0 0 28px rgba(168, 85, 247, 0.35),
      0 6px 18px rgba(0, 0, 0, 0.4);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const DismissBtn = styled.button`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(226, 232, 240, 0.75);
  cursor: pointer;
  padding: 0.35rem;
  line-height: 1;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(239, 68, 68, 0.15);
    border-color: rgba(239, 68, 68, 0.35);
    color: #fecaca;
  }
`;

/**
 * Home: paid subscriptions in renewal window — fixed right-side panel (bottom on small screens).
 * Dismiss is in-memory only: hides until you leave the page or refresh.
 */
const SubscriptionExtendBanner = ({ onExtended }) => {
  const [eligible, setEligible] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [dismissed, setDismissed] = useState({});

  const refresh = useCallback(async () => {
    const user = AuthService.getCurrentUser();
    try {
      if (!user?.accessToken) {
        setEligible([]);
        return;
      }
      if (user?.homeInstitution) {
        setEligible([]);
        setLoading(false);
        return;
      }

      const mine = await subscriptionWarrantyService.getMyEligible();
      if (mine.data?.success) setEligible(mine.data.data || []);
      else setEligible([]);
    } catch {
      setEligible([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    try {
      Object.keys(sessionStorage).forEach((k) => {
        if (k.startsWith('warranty_banner_dismiss_')) sessionStorage.removeItem(k);
      });
    } catch {
      /* ignore */
    }
  }, []);

  const openRazorpay = async (typingCourseId) => {
    const sdkLoaded = await loadRazorpaySdk();
    if (!sdkLoaded || !window.Razorpay) {
      window.alert('Payment is still loading. Please try again in a moment.');
      return;
    }
    setPayingId(typingCourseId);
    try {
      const res = await subscriptionWarrantyService.createExtensionOrder(typingCourseId);
      if (!res.data?.success || !res.data?.order) throw new Error(res.data?.message || 'Could not create order');
      const order = typeof res.data.order === 'string' ? JSON.parse(res.data.order) : res.data.order;

      const options = {
        key: res.data.razorpayKey || process.env.REACT_APP_RAZORPAY_SECRET_KEY,
        amount: order.amount,
        currency: order.currency || 'INR',
        name: 'Typogram',
        description: 'Extend course access',
        order_id: order.id,
        image: logo,
        handler: async (response) => {
          try {
            const confirmRes = await subscriptionWarrantyService.confirmExtension(
              typingCourseId,
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature
            );
            if (confirmRes.data?.success) {
              if (typeof onExtended === 'function') onExtended();
              await refresh();
            } else {
              window.alert(confirmRes.data?.message || 'Could not confirm payment');
            }
          } catch (e) {
            window.alert(e.response?.data?.message || e.message || 'Confirmation failed');
          } finally {
            setPayingId(null);
          }
        },
        theme: { color: '#7c3aed' },
      };

      const rz = new window.Razorpay(options);
      rz.on('payment.failed', () => setPayingId(null));
      rz.on('modal.close', () => setPayingId(null));
      rz.open();
    } catch (e) {
      window.alert(e.response?.data?.message || e.message || 'Payment could not start');
      setPayingId(null);
    }
  };

  if (loading) return null;

  const visible = eligible.filter((row) => {
    const id = row.typingCourseId;
    return !dismissed[id];
  });

  if (visible.length === 0) return null;

  const dismiss = (id) => {
    setDismissed((d) => ({ ...d, [id]: true }));
  };

  return (
    <Dock aria-label="Course access renewal">
      <Panel
        initial={{ opacity: 0, x: 28 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
      >
        <GridBg />
        <ScanSweep />
        <HeaderRow>
          <div>
            <Kicker>Typogram · renewal node</Kicker>
            <Title>
              <FiZap size={18} aria-hidden /> Extend access
            </Title>
          </div>
        </HeaderRow>
        <Blurb>
          Subscription window active — secure extension below. Pricing follows admin defaults or per-course overrides.
        </Blurb>
        <CourseList>
          {visible.map((row) => (
            <CourseCard key={row.typingCourseId}>
              <CourseTitle>{row.subject}</CourseTitle>
              <Meta>
                {row.expiredGrace ? (
                  <>
                    {row.daysPastExpiry === 0
                      ? 'Access ended today · renew below'
                      : `Expired ${row.daysPastExpiry} day${row.daysPastExpiry === 1 ? '' : 's'} ago · renew below`}
                    {row.expiresAt && (
                      <>
                        {' '}
                        · ended {new Date(row.expiresAt).toLocaleString()}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {row.daysLeft === 0 ? 'Expires today' : `${row.daysLeft} day${row.daysLeft === 1 ? '' : 's'} left`} · ends{' '}
                    {row.expiresAt ? new Date(row.expiresAt).toLocaleString() : ''}
                  </>
                )}
                {row.extensionDaysToAdd != null && (
                  <span style={{ display: 'block', marginTop: '0.2rem', color: 'rgba(34, 211, 238, 0.95)' }}>
                    +{row.extensionDaysToAdd} days after payment
                  </span>
                )}
              </Meta>
              <Actions>
                <PayBtn
                  type="button"
                  disabled={payingId != null}
                  onClick={() => openRazorpay(row.typingCourseId)}
                >
                  {payingId === row.typingCourseId ? (
                    <>
                      <ClipLoader color="#0c0118" size={14} /> Processing
                    </>
                  ) : (
                    `₹${row.extensionPriceInr}`
                  )}
                </PayBtn>
                <DismissBtn type="button" aria-label="Dismiss this course" onClick={() => dismiss(row.typingCourseId)}>
                  <FiX size={16} />
                </DismissBtn>
              </Actions>
            </CourseCard>
          ))}
        </CourseList>
      </Panel>
    </Dock>
  );
};

export default SubscriptionExtendBanner;
